import { create } from 'zustand';
import { JWT_STORAGE_KEY, GATES_STORAGE_KEY } from '../services/authStorageKeys';
import { CSRF_COOKIE_NAME, isAuthEnabled } from '../services/sessionCookieNames';

interface GatesCache {
  isRegistered: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  // Vestigial: kept in shape for backward-compat with consumers that still
  // read `isLoading`, but always `false` now. Gates render from cached
  // booleans + the `isAuthenticated` sentinel synchronously at boot — there
  // is no "blocking" state to wait through anymore.
  isLoading: boolean;
  registeredAt: string | null;
  verifiedAt: string | null;
  passwordSetAt: string | null;
  // Optimistic gate-decider boolean hydrated from localStorage at boot.
  // Authoritative truth is what whoAmI writes back at the end of restore;
  // until that lands, the registration gate renders from this cached value
  // so first paint isn't blocked on the whoami round-trip.
  isRegistered: boolean;
  setAuthenticated: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  setRegisteredAt: (value: string | null) => void;
  setVerifiedAt: (value: string | null) => void;
  setPasswordSetAt: (value: string | null) => void;
  setRegistered: (value: boolean) => void;
  /** Clear the gate boolean + its localStorage cache. Called on logout. */
  clearGates: () => void;
}

/**
 * Synchronously read the cookie-mode auth sentinel — the CSRF cookie (name
 * varies per app per `CSRF_COOKIE_NAME`: `csrf_token` for consumer apps,
 * `csrf_admin_token` for admin) is set by the BE on login, is NOT HttpOnly
 * (JS can read it), and disappears on logout. Presence == logged in.
 *
 * The cookie name is built into a fresh RegExp at module load so the
 * regex matches the env-configured name verbatim — admin reads ONLY its
 * own admin cookie even when the parent-domain consumer cookie is also
 * present in `document.cookie`.
 */
const csrfCookieRegex = new RegExp(
  `(?:^|;\\s*)${CSRF_COOKIE_NAME.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=`,
);

function hasCsrfCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return csrfCookieRegex.test(document.cookie);
}

/**
 * Compute initial `isAuthenticated` from the synchronously-available auth
 * sentinels. In cookie-mode prod that's the per-app CSRF cookie; in
 * localStorage-mode dev it's the JWT key. Either signal being present means
 * we paint the optimistic logged-in shell; the in-flight whoami either
 * confirms or snaps to logged-out via the apiService 401-refresh chain.
 *
 * If `VITE_SESSION_MODE` is empty (`isAuthEnabled === false`, currently
 * only the marketing site `www`), short-circuit to `false` regardless of
 * what cookies are present. www must NEVER read auth state — its parent
 * domain cookies belong to the sibling app subdomains and reading them
 * would falsely paint a logged-in shell on a page with no user surface.
 */
function detectInitialAuth(): boolean {
  if (!isAuthEnabled) return false;
  if (hasCsrfCookie()) return true;
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(JWT_STORAGE_KEY) != null;
  }
  return false;
}

function loadGatesCache(): GatesCache {
  if (typeof localStorage === 'undefined') {
    return { isRegistered: false };
  }
  try {
    const stored = localStorage.getItem(GATES_STORAGE_KEY);
    if (!stored) return { isRegistered: false };
    const parsed = JSON.parse(stored) as Partial<GatesCache>;
    return {
      isRegistered: parsed.isRegistered === true,
    };
  } catch {
    return { isRegistered: false };
  }
}

function saveGatesCache(gates: GatesCache): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(GATES_STORAGE_KEY, JSON.stringify(gates));
  } catch {
    // Quota exceeded / disabled — degrade silently. The in-memory store
    // still has the right values for this session; only the next cold-boot
    // hydration would fall back to defaults.
  }
}

function clearGatesCache(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(GATES_STORAGE_KEY);
}

const initialGates = loadGatesCache();
const initialAuth = detectInitialAuth();

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: initialAuth,
  isLoading: false,
  registeredAt: null,
  verifiedAt: null,
  passwordSetAt: null,
  isRegistered: initialGates.isRegistered,
  setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
  setLoading: (isLoading) => set({ isLoading }),
  setRegisteredAt: (registeredAt) => set({ registeredAt }),
  setVerifiedAt: (verifiedAt) => set({ verifiedAt }),
  setPasswordSetAt: (passwordSetAt) => set({ passwordSetAt }),
  setRegistered: (isRegistered) => {
    saveGatesCache({ isRegistered });
    set({ isRegistered });
  },
  clearGates: () => {
    clearGatesCache();
    set({ isRegistered: false });
  },
}));
